import { useMemo } from 'react';
import { Typography, Box, Link, Alert } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import SignupForm from '../components/auth/SignupForm';
import AuthLayout from '../components/auth/AuthLayout';
import { useLang } from '../contexts/langContext';
import { useBranding } from '../contexts/BrandingContext';

function SignupPage() {
  const { lang } = useLang();
  const { localLoginForm } = useBranding();

  const { registrationLocked, isAdminInitMode, registrationMessage, redirectUrl } = useMemo(() => {
    const config = localLoginForm;
    if (!config) {
      return { registrationLocked: false, isAdminInitMode: false, registrationMessage: undefined, redirectUrl: undefined };
    }

    const defaultLocked = config.locked ?? false;
    const adminLocked = config.admin?.locked ?? defaultLocked;

    // Admin init mode: show form with warning
    if (defaultLocked && !adminLocked) {
      return {
        registrationLocked: false,
        isAdminInitMode: true,
        registrationMessage:
          lang === 'en'
            ? 'This page is for admin initialization only. Regular user registration is disabled.'
            : '此页面仅用于管理员初始化。普通用户注册已禁用。',
        redirectUrl: config.redirectUrl,
      };
    }

    // Fully locked
    if (defaultLocked) {
      const localizedMessage = lang === 'zh-HK' ? config.message?.['zh-CN'] : config.message?.[lang as 'en' | 'zh-CN'];
      return {
        registrationLocked: true,
        isAdminInitMode: false,
        registrationMessage: localizedMessage ?? (lang === 'en' ? 'Registration is disabled.' : '注册已禁用。'),
        redirectUrl: config.redirectUrl,
      };
    }

    return { registrationLocked: false, isAdminInitMode: false, registrationMessage: undefined, redirectUrl: undefined };
  }, [localLoginForm, lang]);

  return (
    <AuthLayout title={lang === 'en' ? 'Sign Up' : '注册'}>
      {/* Fully locked message */}
      {registrationLocked && (
        <Alert severity="warning" variant="outlined" sx={{ mt: 2 }}>
          {registrationMessage}
          {redirectUrl && (
            <span>
              {' '}
              <Link href={redirectUrl} target="_blank" rel="noopener noreferrer" sx={{ fontWeight: 500 }}>
                {lang === 'en' ? 'Click here to continue.' : '点击此处继续'}
              </Link>
            </span>
          )}
        </Alert>
      )}

      {/* Admin init mode warning */}
      {isAdminInitMode && (
        <Alert severity="info" variant="outlined" sx={{ mt: 2, mb: 2 }}>
          {registrationMessage}
        </Alert>
      )}

      {/* Show form when not fully locked */}
      {!registrationLocked && <SignupForm isAdminInitMode={isAdminInitMode} />}
      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
          {lang === 'en' ? 'Already have an account?' : '已经有一个账户？'}{' '}
          <Link
            component={RouterLink}
            to="/login"
            sx={{
              color: 'primary.main',
              fontWeight: 500,
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            {lang === 'en' ? 'Log in' : '登录'}
          </Link>
        </Typography>
      </Box>
    </AuthLayout>
  );
}

export default SignupPage;
